import { AccountTree, Dashboard, Task } from "@mui/icons-material";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import ViewListSharpIcon from "@mui/icons-material/ViewListSharp";
import React from "react";
import disputeResolution from "../../assets/dispute-resolution.svg";
import { ClientTypeStrings, FrontendActor, UserRole, UserTypes } from "../../entity/models/FrontendActor";
import {
  uiLoggerName,
  uiLoggerNamesPartnerRiskManagement,
  uiLoggerNamesReportsMailAutomation,
} from "../../Utils/Recon/UiLogger/Constants";
import { AdminTopBar, AdminTopBarType, NavBarStructure } from "./Constants";

export function getAdminTopBarButtons(currentPage: AdminTopBarType) {
  return [
    {
      hasSubTab: false,
      name: "Userlist",
      to: "/admin/userlist",
      className: `navbar-item ${currentPage === AdminTopBar.Userlist ? "this-page" : ""}`,
      icon: <i className="fa-solid fa-list-ul" />,
    },
    {
      hasSubTab: false,
      name: "Register",
      to: "/admin/Register",
      icon: <i className="fa-solid fa-user-plus" />,
    },
    {
      hasSubTab: false,
      name: "Sap Data",
      to: "/admin/sapdata",
      icon: <i className="fa-solid fa-server" />,
    },
    {
      hasSubTab: false,
      name: "Ledger Data",
      to: "/admin/ledgerdata",
      icon: <i className="fas fa-server" />,
    },
    {
      hasSubTab: false,
      name: "Config Templates",
      to: "/admin/configtemplates",
      icon: <i className="fa-solid fa-file-pen" />,
    },
    {
      hasSubTab: false,
      name: "Preprocessing Scripts",
      to: "/admin/scripts",
      icon: <i className="fa-solid fa-file-contract" />,
    },
    {
      hasSubTab: false,
      name: "Tally Voucher",
      to: "/admin/tallyvoucher",
      icon: <i className="fa-solid fa-file-contract" />,
    },
    {
      hasSubTab: false,
      name: "Tally Ledger",
      to: "/admin/tallyledger",
      icon: <i className="fa-solid fa-file-invoice" />,
    },
    {
      hasSubTab: false,
      name: "CB Update",
      to: "/admin/CBUpdate",
      icon: <i className="fa-solid fa-pen-to-square" />,
    },
    {
      hasSubTab: false,
      name: "Unpaid Invoice",
      to: "/admin/UnpaidInvoice",
      icon: <i className="fa-solid fa-file-invoice-dollar" />,
    },
    {
      hasSubTab: false,
      name: "Payment Details",
      to: "/admin/PaymentDetails",
      icon: <i className="fa-solid fa-money-check-dollar" />,
    },
    {
      hasSubTab: true,
      name: "Analytics",
      icon: <AccountTree />,
      subMenu: [
        {
          name: "Recon Analytics",
          to: `/admin/reconAnalytics`,
          icon: <i className="fas fa-magic" />,
        },
        {
          name: "PC Analytics",
          to: `/admin/partnerCommunicationAnalytics`,
          icon: <i className="fas fa-envelope" />,
        },
        {
          name: "Task Management",
          to: `/admin/taskManagement`,
          icon: <i className="fa-solid fa-list-check" />,
        },
        {
          name: "Consolidated MIS",
          to: `/admin/consolidatedMIS`,
          icon: <i className="fa-solid fa-list-check" />,
        },
      ],
    },
  ] as NavBarStructure[];
}

export function AuditCommunicationShown(actor: FrontendActor) {
  // If client_type is one of {D2C Service, DR Service, Service (Others), POC, Internal, Account id{862390, 869489} then V2

  const allowedClientTypes: ClientTypeStrings[] = ["D2C Service", "DR Service", "Service (Others)", "POC", "Internal"];
  const allowedAccountIds = [862390, 869489];

  return allowedClientTypes.includes(actor.clientType) || allowedAccountIds.includes(actor.id);
}

export function getReconTopBarButtons(
  _pageName: string,
  _actorName: string,
  _openCollapseObj: Record<string, unknown>,
  actor: FrontendActor
) {
  // const { actor } = useContext(userContext);
  const accountName = actor?.name
    .toString()
    .toLowerCase()
    .replace(/[^a-z\d]+/gi, "");

  let ledgerToolsModule: NavBarStructure[] = [];

  if (actor.integration) {
    ledgerToolsModule = [
      {
        show: !(
          actor.userRole === UserRole.DRMaker ||
          actor.userRole === UserRole.DRChecker ||
          actor.userRole === UserRole.DRApprover
        ), // Hide if "Rejected Invoices" is shown
        hasSubTab: true,
        name: "Ledger Tools",
        icon: <i className="fas fa-chart-line" />,
        subMenu: [
          {
            name: "Ledger Download",
            to: `/${accountName}/LedgerTools/LedgerDownload`,
            icon: <i className="fas fa-file-invoice" />,
          },
          {
            name: "Ledger Upload",
            to: `/${accountName}/LedgerTools/LedgerUpload`,
            icon: <i className="fas fa-file-invoice" />,
            show: [3407255, 885953].includes(actor.id), // Only show for account id related to tata groups
          },
        ],
      },
    ];
  }
  const allNavBarButtons: NavBarStructure[] = [
    {
      show: !(
        actor.userRole === UserRole.DRMaker ||
        actor.userRole === UserRole.DRChecker ||
        actor.userRole === UserRole.DRApprover
      ), // Hide if "Rejected Invoices" is shown
      hasSubTab: false,
      name: "Task Board",
      to: `/${accountName}/taskBoard`,
      icon: (
        <i>
          <ViewListSharpIcon sx={{ height: "28px !important" }} />
        </i>
      ),
    },
    {
      show: !(
        actor.userRole === UserRole.DRMaker ||
        actor.userRole === UserRole.DRChecker ||
        actor.userRole === UserRole.DRApprover
      ), // Hide if "Rejected Invoices" is shown
      hasSubTab: true,
      name: "Partner Communication",
      icon: <i className="fas fa-envelope" />,
      eventName: uiLoggerName.ui_PartnerCommunicationSelected,
      subMenu: [
        {
          name: "Balance Confirmation & Ledger Request",
          to: `/${accountName}/partnerCommunication/balanceConfirmV2`,
          icon: (
            <i>
              <Task sx={{ height: "28px !important" }} />
            </i>
          ),
          show: true,
          // show: import.meta.env.DEV === true || import.meta.env.VITE_APP_NAKAD_ENV === "test",
        },
        {
          name: "MSME Confirmation",
          to: `/${accountName}/partnerCommunication/msmeConfirmation`,
          icon: <i className="fas fa-check-circle" />,
        },
      ],
    },
    {
      show: !(
        actor.userRole === UserRole.DRMaker ||
        actor.userRole === UserRole.DRChecker ||
        actor.userRole === UserRole.DRApprover
      ), // Hide if "Rejected Invoices" is shown
      hasSubTab: true,
      name: "Automated Recon",
      icon: <i className="fas fa-magic" />,
      subMenu: [
        {
          name: "Ledger",
          to: `/${accountName}/recon360/Summary/Ledger`,
          icon: <i className="fas fa-file-invoice" />,
        },
        {
          name: "Payment Advice",
          to: `/${accountName}/recon360/Summary/PaymentAdvice`,
          icon: <i className="fas fa-receipt" />,
          show: !(
            import.meta.env.VITE_APP_NAKAD_ENV === "production" || import.meta.env.VITE_APP_NAKAD_ENV === "staging"
          ),
        },
      ],
    },
    {
      hasSubTab: true,
      name: "Dispute Resolution",
      icon: (
        <i className="fad">
          <img src={disputeResolution} alt="Dispute Resolution Icon" style={{ height: "30px", width: "30px" }} />
        </i>
      ),
      subMenu: [
        {
          show: !(
            actor.userRole === UserRole.DRMaker ||
            actor.userRole === UserRole.DRChecker ||
            actor.userRole === UserRole.DRApprover
          ), // Hide if "Rejected Invoices" is shown
          name: "Closure Tracker",
          to: `/${accountName}/disputeResolution/closureTracker`,
          icon: <i className="fa-solid fa-clipboard-check" />,
        },
        {
          show:
            actor.userRole === UserRole.DRMaker ||
            actor.userRole === UserRole.DRChecker ||
            actor.userRole === UserRole.DRApprover ||
            actor.userRole === UserRole.ReconDRApprover, // Only show for DR roles
          name: "Rejected Invoices",
          to: `/${accountName}/disputeResolution/rejectedInvoices`,
          icon: <i className="fa-solid fa-clipboard-check" />,
        },
        {
          show:
            actor.userRole === UserRole.DRMaker ||
            actor.userRole === UserRole.DRChecker ||
            actor.userRole === UserRole.DRApprover ||
            actor.userRole === UserRole.ReconDRApprover, // Only show for DR roles

          name: "Disputed Entries",
          to: `/${accountName}/disputeResolution/disputedEntries`,
          icon: <i className="fa-solid fa-copy" />,
        },
        {
          show:
            actor.userRole === UserRole.DRMaker ||
            actor.userRole === UserRole.DRChecker ||
            actor.userRole === UserRole.DRApprover ||
            actor.userRole === UserRole.ReconDRApprover,

          name: "Vendor Hold",
          to: `/${accountName}/disputeResolution/vendorHold`,
          icon: <i className="fa-solid fa-hand" />,
        },
      ],
    },
    ...ledgerToolsModule,
    {
      hasSubTab: true,
      name: "Recon MIS",
      icon: (
        <i>
          <Dashboard />
        </i>
      ),
      show:
        actor.userRole !== UserRole.Maker &&
        !(
          actor.userRole === UserRole.DRMaker ||
          actor.userRole === UserRole.DRChecker ||
          actor.userRole === UserRole.DRApprover
        ), // Hide if "Rejected Invoices" is shown
      subMenu: [
        {
          name: "Partner Risk Management",
          to: `/${accountName}/ReconMIS/partnerRiskManagement`,
          icon: <i className="fa-solid fa-user-gear" />,
          eventName: uiLoggerNamesPartnerRiskManagement.UI_MIS_PARTNERRISKMANAGEMENT_CLICK.functionName,
        },
        {
          name: "Reports Mail Automation",
          to: `/${accountName}/ReconMIS/reportsMailAutomation`,
          icon: (
            <i>
              <AttachEmailIcon sx={{ height: "28px !important" }} />
            </i>
          ),
          eventName: uiLoggerNamesReportsMailAutomation.UI_MIS_REPORTSMAILAUTOMATION_CLICK.functionName,
          show: actor.userType === UserTypes.CustomerSuccess,
        },
      ],
    },
    {
      show: !(
        actor.userRole === UserRole.DRMaker ||
        actor.userRole === UserRole.DRChecker ||
        actor.userRole === UserRole.DRApprover
      ), // Hide if "Rejected Invoices" is shown
      hasSubTab: false,
      name: "Partner Master",
      to: `/${accountName}/partnerMaster`,
      icon: <i className="fa-solid fa-user-group" />,
    },
    {
      hasSubTab: false,
      name: "Settings",
      to: `/${accountName}/reconSettings`,
      icon: <i className="fas fa-cog" />,
      show:
        actor.userRole !== UserRole.Maker &&
        !(
          actor.userRole === UserRole.DRMaker ||
          actor.userRole === UserRole.DRChecker ||
          actor.userRole === UserRole.DRApprover
        ), // Hide if "Rejected Invoices" is shown
    },
    {
      hasSubTab: false,
      name: "Supply Chain Dashboard",
      to: `/${accountName}/supplierchaindashboard`,
      icon: <i className="fa-solid fa-box" />,
      show: !(
        import.meta.env.VITE_APP_NAKAD_ENV === "production" ||
        import.meta.env.VITE_APP_NAKAD_ENV === "staging" ||
        actor.userRole === UserRole.DRMaker ||
        actor.userRole === UserRole.DRChecker ||
        actor.userRole === UserRole.DRApprover
      ),
    },
  ];

  const filteredSubMenu = allNavBarButtons?.map((navBarBtns) => {
    if (navBarBtns?.subMenu?.length > 0) {
      navBarBtns.subMenu = navBarBtns.subMenu.filter((subMenu) => (subMenu.show === undefined ? true : subMenu.show));
      return navBarBtns;
    } else return navBarBtns;
  });

  const filteredNavBarButtons = filteredSubMenu?.filter((button) => {
    if (button.show === undefined) {
      return true;
    }
    return button.show;
  });

  return filteredNavBarButtons;
}
